import { useMutation, useQuery } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { Suspense, lazy, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";

import { queryKeys } from "~/api/queryKeys";
import Button from "~/components/ui/button/Button.tsx";
import { useAuth } from "~/contexts/AuthProvider.tsx";
import { canMember } from "~/helpers/authorizations";
import { useImpersonation } from "~/hooks/useImpersonation";
import { getBrandName } from "~/utils/white-label";

import { PasswordReset, UserAttributes } from "../../api/Api";
import { apiClient } from "../../api/ApiClient";
import FormBaseErrors from "../../components/ui/formBaseErrors/FormBaseErrors";
import Input from "../../components/ui/input/Input";
import { EMAIL_VALIDATION_REG_EX } from "../../helpers/helpers";
import labels from "../../helpers/labels";
import { tryCatchParsed } from "../../helpers/tryCatch";
import { useFormErrors } from "../../hooks/useFormErrors";
import FormLabel from "../login/formLabel/FormLabel";
import PasswordInput from "../login/passwordInput/PasswordInput";
import Modal from "../ui/modal/Modal";
import styles from "./Settings.module.scss";
import DisableMfaModal from "./twoFactor/DisableMfaModal";

// Lazy-load only `EnableMfaModal` — it pulls in the `qrcode` library
// (~50KB gzipped) which would otherwise be eagerly bundled into the settings
// chunk for every user, including the majority who already have MFA enabled
// and will never re-enroll. `DisableMfaModal` is a small confirm dialog with
// no heavy deps, so it stays eager.
// The factory is extracted so the click handler can `await` the chunk before
// flipping the modal open — on slow networks this lets us render a spinner
// inside the Enable button instead of leaving the user staring at an empty
// page while the JS downloads.
const importEnableMfaModal = () => import("./twoFactor/EnableMfaModal");
const EnableMfaModal = lazy(importEnableMfaModal);

interface PasswordFormValues extends PasswordReset {
  current_password: string;
}

const AccountSecurity = () => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isEnableMfaOpen, setIsEnableMfaOpen] = useState(false);
  const [isDisableMfaOpen, setIsDisableMfaOpen] = useState(false);
  // Track whether the Enable modal has been opened at least once. After the
  // first open the lazy chunk is loaded and the modal stays mounted so its
  // close transition runs (matches the change-password modal's behaviour).
  const [enableMfaMounted, setEnableMfaMounted] = useState(false);
  // True while the lazy `EnableMfaModal` chunk is downloading after the user
  // clicks Enable. Drives the button spinner so slow connections do not
  // appear unresponsive.
  const [isEnableMfaLoading, setIsEnableMfaLoading] = useState(false);

  const { token, user, authorizations: userAuthorizations } = useAuth();
  const { isImpersonating } = useImpersonation();
  const canUpdateUser = canMember(userAuthorizations, "update");
  const mfaEnabled = user?.mfa_enabled ?? false;

  const mfaStatusQuery = useQuery({
    queryKey: queryKeys.mfaStatus(token),
    // Skip the status fetch entirely while impersonating — the section is
    // hidden behind a notice and the response carries setup data we never
    // want to expose to a support agent acting as another user.
    enabled: mfaEnabled && !isImpersonating,
    queryFn: async () => (await apiClient.mfa.getMfa()).data,
    // Backup-code count only changes via our own mutations (enable/disable),
    // which explicitly remove this query. Treat as fresh forever to avoid
    // refetches on every settings tab visit.
    staleTime: Infinity,
  });

  const mfaStatus = mfaStatusQuery.data?.mfa;
  const backupCodesRemaining =
    mfaStatus?.enrolled === true ? mfaStatus.backup_codes_remaining : null;

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
    getValues: getPasswordValues,
    reset: resetPasswordForm,
    setError,
  } = useForm<PasswordFormValues>({
    defaultValues: {
      reset_password_token: "",
      password: "",
      password_confirmation: "",
      current_password: "",
    },
  });
  const { baseErrors, applyServerErrors, clearBaseErrors } =
    useFormErrors<PasswordFormValues>(setError);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: Partial<UserAttributes>) =>
      apiClient.user.userPartialUpdate(data as UserAttributes),
  });

  const onPasswordSubmit = async (data: PasswordFormValues) => {
    const result = await tryCatchParsed(
      mutateAsync({
        password: data.password,
        password_confirmation: data.password_confirmation,
        current_password: data.current_password,
      }),
    );

    if (result.error) {
      applyServerErrors(result.error);

      return;
    }

    enqueueSnackbar("Password updated successfully", { variant: "success" });
    setIsPasswordModalOpen(false);
  };

  const openPasswordModal = () => {
    resetPasswordForm();
    clearBaseErrors();
    setIsPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    clearBaseErrors();
    setIsPasswordModalOpen(false);
  };

  return (
    // @ts-expect-error TS2551 non-existing className
    <div className={styles.settingsSectionGroup}>
      <p className={styles.settingsText}>
        Allows you to view general information about your account and manage its
        settings. Empowers users to oversee various aspects of their count
        configuration
      </p>
      <div className={styles.settingsForm}>
        <div className={styles.formRow}>
          <div>
            <FormLabel label="First Name" />
            <Input
              disabled={!canUpdateUser}
              {...register("first_name", {
                required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
                validate: {
                  notEmpty: (value) =>
                    value.trim() !== "" || labels.THIS_FIELD_CANNOT_BE_EMPTY,
                },
              })}
              errormessage={
                errors.first_name ? (errors.first_name.message as string) : ""
              }
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div>
            <FormLabel label="Last Name" />
            <Input
              disabled={!canUpdateUser}
              {...register("last_name", {
                required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
                validate: {
                  notEmpty: (value) =>
                    value.trim() !== "" || labels.THIS_FIELD_CANNOT_BE_EMPTY,
                },
              })}
              errormessage={errors.last_name?.message as string}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div>
            <FormLabel label="Email" />
            <Input
              disabled={!canUpdateUser}
              {...register("email", {
                required: labels.THIS_FIELD_CANNOT_BE_EMPTY,
                validate: {
                  notEmpty: (value) =>
                    value.trim() !== "" || labels.THIS_FIELD_CANNOT_BE_EMPTY,
                },
                pattern: {
                  value: EMAIL_VALIDATION_REG_EX,
                  message: labels.INVALID_EMAIL,
                },
              })}
              errormessage={errors.email?.message as string}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div>
            <FormLabel label="Password" />
            <p className={styles.settingsText}>
              In case of security measures, you can change your password
              anytime. This allows you to maintain control over access to your
              account
            </p>
          </div>
          <Button
            outlined
            disabled={!canUpdateUser}
            className={styles.settingsButton}
            onClick={openPasswordModal}
          >
            Change Password
          </Button>
        </div>

        {/* Two-Factor Authentication is hidden entirely while impersonating.
            Reasoning:
              - `mfa.getMfa()` would expose the target user's enrollment state
                and (when not yet enrolled) their `provisioning_uri` containing
                the TOTP secret — a support agent must never see that.
              - Enable / Disable both let the agent silently alter the target
                user's second factor without their consent.
            We render a notice in place of the controls so the section's
            absence is explicit rather than mysterious. */}
        {isImpersonating ? (
          <div className={styles.formRow}>
            <div>
              <FormLabel label="Two-Factor Authentication" />
              <p className={styles.settingsText}>Hidden while impersonating</p>
            </div>
          </div>
        ) : (
          <div className={styles.formRow}>
            <div>
              <FormLabel label="Two-Factor Authentication" />
              <p className={styles.settingsText}>
                {mfaEnabled
                  ? "Two-factor authentication is enabled"
                  : "Add an extra layer of security to your sign-in. Each time you log in, you'll enter a one-time code from an authenticator app like Google Authenticator, Authy, or 1Password."}
              </p>
              {/* Backup-code count loads asynchronously from `mfa` —
                  render it as a separate line so the primary message above
                  does not reflow once the count arrives. */}
              {mfaEnabled && backupCodesRemaining !== null && (
                <p className={styles.settingsText}>
                  {backupCodesRemaining} backup codes remaining
                </p>
              )}
            </div>
            <Button
              outlined
              disabled={!canUpdateUser}
              withSpinner={isEnableMfaLoading}
              className={styles.settingsButton}
              onClick={async () => {
                if (mfaEnabled) {
                  setIsDisableMfaOpen(true);

                  return;
                }
                // Preload the chunk before mounting the modal. Once this
                // resolves the module is in the bundler cache, so `<Suspense>`
                // resolves synchronously and there is no second flash of
                // emptiness between the spinner and the rendered modal.
                setIsEnableMfaLoading(true);
                try {
                  await importEnableMfaModal();
                  setEnableMfaMounted(true);
                  setIsEnableMfaOpen(true);
                } catch {
                  // Chunk download failed (network drop, expired hash after a
                  // deploy, etc.). Surface a recoverable message instead of
                  // leaving the spinner spinning forever.
                  enqueueSnackbar(
                    "Could not load the two-factor setup. Please try again.",
                    { variant: "error" },
                  );
                } finally {
                  setIsEnableMfaLoading(false);
                }
              }}
            >
              {mfaEnabled ? "Disable Two-Factor" : "Enable Two-Factor"}
            </Button>
          </div>
        )}
      </div>

      <Modal
        isOpen={isPasswordModalOpen}
        closeModal={closePasswordModal}
        modalLabel={<h3>Change Password</h3>}
        buttonLabel="Change Password"
        action={() => handleSubmitPassword(onPasswordSubmit)()}
        actionButtonColor="primary"
        isLoading={isPending}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmitPassword(onPasswordSubmit)();
          }}
        >
          <p>
            Update your password for {getBrandName()}. Going forward, you'll
            need this new password to log into the system
          </p>
          <FormBaseErrors errors={baseErrors} />
          {/*
           // @ts-expect-error TS2339 non-existing className */}
          <div className={styles.passwordModalFields}>
            <PasswordInput
              passwordError={passwordErrors.current_password?.message}
              //@ts-expect-error fix <PasswordInput>
              register={registerPassword}
              label="Current Password"
              //@ts-expect-error fix <PasswordInput>
              field="current_password"
              //@ts-expect-error fix <PasswordInput>
              getValues={getPasswordValues}
            />

            <div className={styles.inputsWrapper}>
              <PasswordInput
                passwordError={passwordErrors.password?.message}
                //@ts-expect-error fix <PasswordInput>
                register={registerPassword}
                label={labels.NEW_PASSWORD}
                field="password"
                //@ts-expect-error fix <PasswordInput>
                getValues={getPasswordValues}
              />
              <PasswordInput
                passwordError={passwordErrors.password_confirmation?.message}
                //@ts-expect-error fix <PasswordInput>
                register={registerPassword}
                label={labels.CONFIRM_PASSWORD}
                field="password_confirmation"
                //@ts-expect-error fix <PasswordInput>
                getValues={getPasswordValues}
              />
            </div>
          </div>
        </form>
      </Modal>

      {enableMfaMounted && (
        <Suspense fallback={null}>
          <EnableMfaModal
            isOpen={isEnableMfaOpen}
            onClose={() => setIsEnableMfaOpen(false)}
          />
        </Suspense>
      )}
      <DisableMfaModal
        isOpen={isDisableMfaOpen}
        onClose={() => setIsDisableMfaOpen(false)}
      />
    </div>
  );
};

export default AccountSecurity;
